// src/schema-form.tsx
import { zodResolver } from "@hookform/resolvers/zod";
import * as React3 from "react";
import { FormProvider, useForm } from "react-hook-form";
import {
  Form as ReactRouterForm,
  useActionData,
  useNavigation,
  useSubmit
} from "react-router";

// src/children-traversal.ts
import * as React from "react";
function mapChildren(children, fn) {
  return React.Children.map(children, (child) => {
    if (!React.isValidElement(child)) return child;
    const mapped = fn(child);
    if (child.props.children && typeof child.props.children !== "function") {
      const mappedChildren = mapChildren(child.props.children, fn);
      return mapped && React.cloneElement(mapped, { children: mappedChildren });
    }
    return mapped;
  });
}
function findElement(root, predicate) {
  const initialState = null;
  return reduceElements(
    root,
    initialState,
    (previous, current) => {
      if (previous) return previous;
      if (predicate(current)) {
        return current;
      }
      return null;
    }
  );
}
function findParent(root, child) {
  return findElement(root, (parentCandidate) => {
    var _a, _b, _c;
    const me = (_c = (_b = (_a = parentCandidate.props) == null ? void 0 : _a.children) == null ? void 0 : _b.find) == null ? void 0 : _c.call(
      _b,
      (ch) => {
        return ch === child;
      }
    );
    return !!me;
  });
}
function reduceElements(children, initialState, reducer) {
  let foldedValue = initialState;
  React.Children.forEach(children, (child) => {
    if (!React.isValidElement(child)) return;
    foldedValue = reducer(foldedValue, child);
    if (child.props.children && typeof child.props.children !== "function") {
      foldedValue = reduceElements(child.props.children, foldedValue, reducer);
    }
  });
  return foldedValue;
}

// src/create-field.tsx
import * as React2 from "react";

// src/shape-info.ts
function shapeInfo(shape, optional = false, nullable = false, getDefaultValue, enumValues) {
  if (!shape) {
    return { typeName: null, optional, nullable, getDefaultValue, enumValues };
  }
  const typeName = shape._def.typeName;
  if (typeName === "ZodEffects") {
    return shapeInfo(
      shape._def.schema,
      optional,
      nullable,
      getDefaultValue,
      enumValues
    );
  }
  if (typeName === "ZodOptional") {
    return shapeInfo(
      shape._def.innerType,
      true,
      nullable,
      getDefaultValue,
      enumValues
    );
  }
  if (typeName === "ZodNullable") {
    return shapeInfo(
      shape._def.innerType,
      optional,
      true,
      getDefaultValue,
      enumValues
    );
  }
  if (typeName === "ZodDefault") {
    return shapeInfo(
      shape._def.innerType,
      optional,
      nullable,
      shape._def.defaultValue,
      enumValues
    );
  }
  if (typeName === "ZodEnum") {
    return {
      typeName,
      optional,
      nullable,
      getDefaultValue,
      enumValues: shape._def.values
    };
  }
  return { typeName, optional, nullable, getDefaultValue, enumValues };
}

// src/coercions.ts
function makeCoercion(coercion, emptyValue) {
  return ({
    value,
    optional,
    nullable
  }) => {
    if (value) return coercion(value);
    if (nullable) return null;
    if (optional) return void 0;
    return emptyValue;
  };
}
var coerceString = makeCoercion(String, "");
var coerceNumber = makeCoercion(Number, null);
var coerceBoolean = makeCoercion(Boolean, false);
var coerceDate = makeCoercion((value) => {
  if (typeof value !== "string") return null;
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year, month - 1, day);
}, null);
function coerceValue(value, shape) {
  const { typeName, optional, nullable } = shapeInfo(shape);
  if (typeName === "ZodBoolean") {
    return coerceBoolean({ value, optional, nullable });
  }
  if (typeName === "ZodNumber") {
    return coerceNumber({ value, optional, nullable });
  }
  if (typeName === "ZodDate") {
    return coerceDate({ value, optional, nullable });
  }
  if (typeName === "ZodString" || typeName === "ZodEnum") {
    return coerceString({ value, optional, nullable });
  }
  return value;
}

// src/prelude.ts
function objectFromSchema(schema) {
  return "shape" in schema ? schema : objectFromSchema(schema._def.schema);
}
function mapObject(obj, mapFunction) {
  return Object.fromEntries(
    Object.entries(obj).map(([key, value]) => mapFunction(key, value))
  );
}
function parseDate(value) {
  if (!value) return value;
  const dateTime = typeof value === "string" ? value : value.toISOString();
  const [date] = dateTime.split("T");
  return date;
}
function browser() {
  return typeof document === "object";
}

// src/create-field.tsx
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
var types = {
  boolean: "checkbox",
  string: "text",
  number: "text",
  date: "date"
};
function getInputType(type, radio) {
  if (radio) return "radio";
  return types[type];
}
var FieldContext = React2.createContext(void 0);
function useField() {
  const context = React2.useContext(FieldContext);
  if (!context) throw new Error("useField used outside of field context");
  return context;
}
var makeSelectOption = ({ name, value }) => /* @__PURE__ */ jsx("option", { value, children: name }, String(value));
var makeOptionComponents = (fn, options) => options ? options.map(fn) : void 0;
function createSmartInput({
  inputComponent: Input = "input",
  multilineComponent: Multiline = "textarea",
  selectComponent: Select = "select",
  checkboxComponent: Checkbox = "input",
  labelComponent: Label = "label",
  radioComponent: Radio = "input",
  radioWrapperComponent: RadioWrapper = "div"
}) {
  return ({
    fieldType,
    type,
    value,
    autoFocus,
    options,
    multiline,
    radio,
    placeholder,
    registerProps,
    a11yProps,
    ...props
  }) => {
    if (!registerProps) return null;
    const makeRadioOption = (props2, checkedValue) => ({ name: name2, value: value2 }) => {
      const propsWithUniqueId = mapObject(
        props2,
        (key, propValue) => key === "id" ? [key, `${propValue}-${value2}`] : [key, propValue]
      );
      return /* @__PURE__ */ jsxs(RadioWrapper, { children: [
        /* @__PURE__ */ jsx(
          Radio,
          {
            type: "radio",
            value: value2,
            defaultChecked: value2 === checkedValue,
            ...propsWithUniqueId
          }
        ),
        /* @__PURE__ */ jsx(Label, { htmlFor: String(propsWithUniqueId == null ? void 0 : propsWithUniqueId.id), children: name2 })
      ] }, String(propsWithUniqueId == null ? void 0 : propsWithUniqueId.id));
    };
    const { name } = registerProps;
    const commonProps = {
      id: name,
      autoFocus,
      ...registerProps,
      ...props
    };
    return fieldType === "boolean" ? /* @__PURE__ */ jsx(
      Checkbox,
      {
        type: "checkbox",
        placeholder,
        defaultChecked: Boolean(value),
        ...commonProps,
        ...a11yProps
      }
    ) : options && !radio ? /* @__PURE__ */ jsx(Select, { defaultValue: value, ...commonProps, ...a11yProps, children: makeOptionComponents(makeSelectOption, options) }) : options && radio ? /* @__PURE__ */ jsx(Fragment, { children: makeOptionComponents(makeRadioOption(commonProps, value), options) }) : multiline ? /* @__PURE__ */ jsx(
      Multiline,
      {
        placeholder,
        defaultValue: value,
        ...commonProps,
        ...a11yProps
      }
    ) : /* @__PURE__ */ jsx(
      Input,
      {
        type,
        placeholder,
        defaultValue: value,
        ...commonProps,
        ...a11yProps
      }
    );
  };
}
function createField({
  register,
  fieldComponent: Field = "div",
  labelComponent: Label = "label",
  inputComponent: Input = "input",
  multilineComponent: Multiline = "textarea",
  selectComponent: Select = "select",
  radioComponent: Radio = "input",
  checkboxComponent: Checkbox = "input",
  checkboxWrapperComponent: CheckboxWrapper = "div",
  radioGroupComponent: RadioGroup = "fieldset",
  radioWrapperComponent: RadioWrapper = "div",
  fieldErrorsComponent: Errors = "div",
  errorComponent: Error2 = "div"
}) {
  return React2.forwardRef(
    ({
      fieldType = "string",
      shape,
      name,
      label,
      options,
      errors,
      dirty,
      type: typeProp,
      required = false,
      autoFocus = false,
      value: rawValue,
      multiline = false,
      radio = false,
      placeholder,
      hidden = false,
      children: childrenFn,
      ...props
    }, ref) => {
      const value = fieldType === "date" ? parseDate(rawValue) : rawValue;
      const field = {
        dirty,
        autoFocus,
        errors,
        fieldType,
        hidden,
        label,
        multiline,
        options,
        placeholder,
        radio,
        required,
        shape,
        value
      };
      const errorsChildren = (errors == null ? void 0 : errors.length) ? errors.map((error) => /* @__PURE__ */ jsx(Error2, { children: error }, error)) : void 0;
      const style = hidden ? { display: "none" } : void 0;
      const type = typeProp != null ? typeProp : getInputType(fieldType, radio);
      const { ref: registerRef, ...registerProps } = register(String(name), {
        setValueAs: (value2) => coerceValue(value2, shape)
      });
      const labelId = `label-for-${name.toString()}`;
      const errorsId = `errors-for-${name.toString()}`;
      const a11yProps = {
        "aria-labelledby": labelId,
        "aria-invalid": Boolean(errors),
        "aria-describedby": errors ? errorsId : void 0,
        "aria-required": required
      };
      const SmartInput = React2.useMemo(
        () => createSmartInput({
          inputComponent: Input,
          multilineComponent: Multiline,
          selectComponent: Select,
          checkboxComponent: Checkbox,
          radioComponent: Radio,
          radioWrapperComponent: RadioWrapper,
          labelComponent: Label
        }),
        []
      );
      if (childrenFn) {
        const childrenDefinition = childrenFn({
          Label,
          SmartInput,
          Input,
          Multiline,
          Select,
          Checkbox,
          Radio,
          RadioGroup,
          RadioWrapper,
          CheckboxWrapper,
          Errors,
          Error: Error2,
          ref,
          name,
          type,
          ...field
        });
        const children = mapChildren(childrenDefinition, (child) => {
          const mergedRef = (instance) => {
            registerRef(instance);
            const { ref: childRef } = child;
            if (childRef) {
              childRef.current = instance;
            }
          };
          if (child.type === Label) {
            return React2.cloneElement(child, {
              id: labelId,
              htmlFor: String(name),
              children: label,
              ...child.props
            });
          }
          if (child.type === SmartInput) {
            const smartInputProps = {
              fieldType,
              type,
              options,
              multiline,
              radio,
              placeholder,
              registerProps: { ...registerProps, ref: mergedRef },
              autoFocus,
              value,
              a11yProps
            };
            return React2.cloneElement(child, {
              ...smartInputProps,
              ...child.props
            });
          }
          if (child.type === Input) {
            return React2.cloneElement(child, {
              id: String(name),
              type,
              ...registerProps,
              ...a11yProps,
              placeholder,
              autoFocus,
              defaultValue: value,
              ...child.props,
              ref: mergedRef
            });
          }
          if (child.type === Multiline) {
            return React2.cloneElement(child, {
              id: String(name),
              ...registerProps,
              ...a11yProps,
              placeholder,
              autoFocus,
              defaultValue: value,
              ...child.props,
              ref: mergedRef
            });
          }
          if (child.type === Select) {
            return React2.cloneElement(child, {
              id: String(name),
              ...registerProps,
              ...a11yProps,
              autoFocus,
              defaultValue: value,
              children: makeOptionComponents(makeSelectOption, options),
              ...child.props,
              ref: mergedRef
            });
          }
          if (child.type === Checkbox && (child.type !== "input" || child.props.type === "checkbox")) {
            return React2.cloneElement(child, {
              id: String(name),
              type,
              autoFocus,
              ...registerProps,
              ...a11yProps,
              placeholder,
              defaultChecked: Boolean(value),
              ...child.props,
              ref: mergedRef
            });
          }
          if (child.type === RadioGroup) {
            return React2.cloneElement(child, {
              ...a11yProps,
              ...child.props
            });
          }
          if (child.type === Radio && (child.type !== "input" || child.props.type === "radio")) {
            return React2.cloneElement(child, {
              id: `${String(name)}-${child.props.value}`,
              type: "radio",
              autoFocus,
              ...registerProps,
              defaultChecked: value === child.props.value,
              ...child.props,
              ref: mergedRef
            });
          }
          if (child.type === Errors) {
            if (!child.props.children && !(errors == null ? void 0 : errors.length)) return null;
            if (child.props.children || !(errors == null ? void 0 : errors.length)) {
              return React2.cloneElement(child, {
                id: errorsId,
                role: "alert",
                ...child.props
              });
            }
            return React2.cloneElement(child, {
              id: errorsId,
              role: "alert",
              children: errorsChildren,
              ...child.props
            });
          }
          return child;
        });
        const fixRadioLabels = (children2) => mapChildren(children2, (child) => {
          var _a;
          if (child.type === Label) {
            const parent = findParent(children2, child);
            if (parent && parent.type === RadioWrapper) {
              const radioChild = findElement(
                (_a = parent.props) == null ? void 0 : _a.children,
                (ch) => ch.type === Radio
              );
              if (radioChild) {
                return React2.cloneElement(child, {
                  htmlFor: radioChild.props.id
                });
              }
            }
          }
          return child;
        });
        return /* @__PURE__ */ jsx(FieldContext.Provider, { value: field, children: /* @__PURE__ */ jsx(Field, { hidden, style, ...props, children: fixRadioLabels(children) }) });
      }
      const smartInput = /* @__PURE__ */ jsx(
        SmartInput,
        {
          fieldType,
          type,
          options,
          multiline,
          radio,
          placeholder,
          registerProps: { ref: registerRef, ...registerProps },
          autoFocus,
          value,
          a11yProps
        }
      );
      return /* @__PURE__ */ jsx(FieldContext.Provider, { value: field, children: /* @__PURE__ */ jsxs(Field, { hidden, style, ...props, children: [
        fieldType === "boolean" ? /* @__PURE__ */ jsxs(CheckboxWrapper, { children: [
          smartInput,
          /* @__PURE__ */ jsx(Label, { id: labelId, htmlFor: String(name), children: label })
        ] }) : radio ? /* @__PURE__ */ jsxs(Fragment, { children: [
          /* @__PURE__ */ jsx(Label, { id: labelId, children: label }),
          /* @__PURE__ */ jsx(RadioGroup, { ...a11yProps, children: smartInput })
        ] }) : /* @__PURE__ */ jsxs(Fragment, { children: [
          /* @__PURE__ */ jsx(Label, { id: labelId, htmlFor: String(name), children: label }),
          smartInput
        ] }),
        Boolean(errorsChildren) && /* @__PURE__ */ jsx(Errors, { role: "alert", id: errorsId, children: errorsChildren })
      ] }) });
    }
  );
}

// src/default-render-field.tsx
import { jsx as jsx2 } from "react/jsx-runtime";
function defaultRenderField({
  Field,
  name,
  ...props
}) {
  return /* @__PURE__ */ jsx2(Field, { name, ...props }, String(name));
}

// src/infer-label.ts
function startCase(str) {
  var _a;
  const matches = (_a = str.match(
    /[A-Z]{2,}(?=[A-Z][a-z]+[0-9]*|\b)|[A-Z]?[a-z]+[0-9]*|[A-Z]|[0-9]+/g
  )) != null ? _a : [""];
  return matches.map((x) => x.charAt(0).toUpperCase() + x.slice(1)).join(" ");
}
function inferLabel(fieldName) {
  return startCase(fieldName).replace(/Url/g, "URL");
}

// src/schema-form.tsx
import { Fragment as Fragment2, jsx as jsx3, jsxs as jsxs2 } from "react/jsx-runtime";
var fieldTypes = {
  ZodString: "string",
  ZodNumber: "number",
  ZodBoolean: "boolean",
  ZodDate: "date",
  ZodEnum: "string"
};
function coerceToForm(value, shape) {
  var _a;
  const { typeName } = shape;
  if (typeName === "ZodBoolean") {
    return (_a = Boolean(value)) != null ? _a : false;
  }
  if (typeName === "ZodDate") {
    return parseDate(value);
  }
  if (typeName === "ZodEnum" || typeName === "ZodString" || typeName === "ZodNumber") {
    return String(value != null ? value : "");
  }
  return value != null ? value : "";
}
function SchemaForm({
  component = ReactRouterForm,
  fetcher,
  mode = "onSubmit",
  reValidateMode = "onChange",
  renderField = defaultRenderField,
  fieldComponent,
  globalErrorsComponent: Errors = "div",
  errorComponent: Error2 = "div",
  fieldErrorsComponent,
  labelComponent,
  inputComponent,
  multilineComponent,
  selectComponent,
  checkboxComponent,
  radioComponent,
  checkboxWrapperComponent,
  radioGroupComponent,
  radioWrapperComponent,
  buttonComponent: Button = "button",
  buttonLabel: rawButtonLabel = "OK",
  pendingButtonLabel,
  method = "POST",
  schema,
  beforeChildren,
  onNavigation,
  children: childrenFn,
  labels,
  placeholders,
  options,
  inputTypes,
  emptyOptionLabel = "",
  hiddenFields,
  multiline,
  radio,
  autoFocus: autoFocusProp,
  errors: errorsProp,
  values: valuesProp,
  flushSync,
  ...props
}) {
  var _a, _b;
  const Component = (_a = fetcher == null ? void 0 : fetcher.Form) != null ? _a : component;
  const navigationSubmit = useSubmit();
  const submit = (_b = fetcher == null ? void 0 : fetcher.submit) != null ? _b : navigationSubmit;
  const navigation = useNavigation();
  const navigationState = fetcher ? fetcher.state : navigation.state;
  const navigationActionData = useActionData();
  const actionData = fetcher ? fetcher.data : navigationActionData;
  const actionErrors = actionData == null ? void 0 : actionData.errors;
  const actionValues = actionData == null ? void 0 : actionData.values;
  const errors = React3.useMemo(
    () => ({ ...errorsProp, ...actionErrors }),
    [errorsProp, actionErrors]
  );
  const values = React3.useMemo(
    () => ({ ...valuesProp, ...actionValues }),
    [valuesProp, actionValues]
  );
  const schemaShape = objectFromSchema(schema).shape;
  const defaultValues = mapObject(schemaShape, (key, fieldShape) => {
    var _a2, _b2;
    const shape = shapeInfo(fieldShape);
    const defaultValue = coerceToForm(
      (_b2 = values[key]) != null ? _b2 : (_a2 = shape == null ? void 0 : shape.getDefaultValue) == null ? void 0 : _a2.call(shape),
      shape
    );
    return [key, defaultValue];
  });
  const form = useForm({
    resolver: zodResolver(schema),
    mode,
    reValidateMode,
    defaultValues
  });
  const { formState, reset } = form;
  const { errors: formErrors, isValid } = formState;
  const formRef = React3.useRef(null);
  const onSubmit = (event) => {
    form.handleSubmit(() => {
      if (!formRef.current) return;
      return submit(formRef.current, {
        method,
        replace: props.replace,
        preventScrollReset: props.preventScrollReset,
        navigate: props.navigate,
        fetcherKey: props.fetcherKey,
        flushSync
      });
    })(event);
  };
  const doSubmit = () => {
    var _a2;
    (_a2 = formRef.current) == null ? void 0 : _a2.dispatchEvent(
      new Event("submit", { cancelable: true, bubbles: true })
    );
  };
  const Field = React3.useMemo(
    () => createField({
      register: form.register,
      fieldComponent,
      labelComponent,
      inputComponent,
      multilineComponent,
      selectComponent,
      checkboxComponent,
      radioComponent,
      checkboxWrapperComponent,
      radioGroupComponent,
      radioWrapperComponent,
      fieldErrorsComponent,
      errorComponent: Error2
    }),
    [
      fieldComponent,
      labelComponent,
      inputComponent,
      multilineComponent,
      selectComponent,
      checkboxComponent,
      radioComponent,
      checkboxWrapperComponent,
      radioGroupComponent,
      radioWrapperComponent,
      fieldErrorsComponent,
      Error2,
      form.register
    ]
  );
  const fieldErrors = React3.useCallback(
    (key) => {
      var _a2;
      const message = (_a2 = formErrors[key]) == null ? void 0 : _a2.message;
      return browser() ? message && [message] : errors == null ? void 0 : errors[key];
    },
    [errors, formErrors]
  );
  const firstErroredField = () => Object.keys(schemaShape).find((key) => {
    var _a2;
    return (_a2 = fieldErrors(key)) == null ? void 0 : _a2.length;
  });
  const makeField = (key) => {
    const shape = schemaShape[key];
    const { typeName, optional, nullable, enumValues } = shapeInfo(shape);
    const required = !(optional || nullable);
    const fieldOptions = (options == null ? void 0 : options[key]) || (enumValues == null ? void 0 : enumValues.map((value) => ({
      name: inferLabel(value),
      value
    })));
    const fieldOptionsPlusEmpty = () => fieldOptions && [
      { name: emptyOptionLabel, value: "" },
      ...fieldOptions != null ? fieldOptions : []
    ];
    return {
      shape,
      fieldType: typeName ? fieldTypes[typeName] : "string",
      type: inputTypes == null ? void 0 : inputTypes[key],
      name: key,
      required,
      dirty: key in formState.dirtyFields,
      label: (labels == null ? void 0 : labels[key]) || inferLabel(String(key)),
      options: required ? fieldOptions : fieldOptionsPlusEmpty(),
      errors: fieldErrors(key),
      autoFocus: key === firstErroredField() || key === autoFocusProp,
      value: defaultValues[key],
      hidden: hiddenFields && Boolean(hiddenFields.find((item) => item === key)),
      multiline: multiline && Boolean(multiline.find((item) => item === key)),
      radio: radio && Boolean(radio.find((item) => item === key)),
      placeholder: placeholders == null ? void 0 : placeholders[key]
    };
  };
  const hiddenFieldsErrorsToGlobal = React3.useCallback(
    (globalErrors2 = []) => {
      const deepHiddenFieldsErrors = hiddenFields == null ? void 0 : hiddenFields.map((hiddenField) => {
        const hiddenFieldErrors = fieldErrors(hiddenField);
        if (Array.isArray(hiddenFieldErrors)) {
          const hiddenFieldLabel = (labels == null ? void 0 : labels[hiddenField]) || inferLabel(String(hiddenField));
          return hiddenFieldErrors.map(
            (error) => `${hiddenFieldLabel}: ${error}`
          );
        }
        return [];
      });
      const hiddenFieldsErrors = (deepHiddenFieldsErrors == null ? void 0 : deepHiddenFieldsErrors.flat()) || [];
      const allGlobalErrors = [].concat(globalErrors2, hiddenFieldsErrors).filter((error) => typeof error === "string");
      return allGlobalErrors.length > 0 ? allGlobalErrors : void 0;
    },
    [fieldErrors, hiddenFields, labels]
  );
  const globalErrors = React3.useMemo(
    () => hiddenFieldsErrorsToGlobal(errors == null ? void 0 : errors._global),
    [errors == null ? void 0 : errors._global, hiddenFieldsErrorsToGlobal]
  );
  const buttonLabel = navigationState !== "idle" ? pendingButtonLabel != null ? pendingButtonLabel : rawButtonLabel : rawButtonLabel;
  const [disabled, setDisabled] = React3.useState(false);
  const globalErrorsToDisplay = navigationState !== "idle" ? void 0 : globalErrors;
  const customChildren = mapChildren(
    childrenFn == null ? void 0 : childrenFn({
      Field,
      Errors,
      Error: Error2,
      Button,
      submit: doSubmit,
      ...form
    }),
    (child) => {
      var _a2;
      if (child.type === Field) {
        const { name } = child.props;
        const field = makeField(name);
        const autoFocus = firstErroredField() ? field == null ? void 0 : field.autoFocus : (_a2 = child.props.autoFocus) != null ? _a2 : field == null ? void 0 : field.autoFocus;
        if (!child.props.children && field) {
          return renderField({
            Field,
            ...field,
            ...child.props,
            autoFocus
          });
        }
        return React3.cloneElement(child, {
          shape: field == null ? void 0 : field.shape,
          fieldType: field == null ? void 0 : field.fieldType,
          label: field == null ? void 0 : field.label,
          placeholder: field == null ? void 0 : field.placeholder,
          required: field == null ? void 0 : field.required,
          options: field == null ? void 0 : field.options,
          value: field == null ? void 0 : field.value,
          errors: field == null ? void 0 : field.errors,
          hidden: field == null ? void 0 : field.hidden,
          multiline: field == null ? void 0 : field.multiline,
          ...child.props,
          autoFocus
        });
      }
      if (child.type === Errors) {
        if (!child.props.children && !(globalErrorsToDisplay == null ? void 0 : globalErrorsToDisplay.length)) return null;
        if (child.props.children || !(globalErrorsToDisplay == null ? void 0 : globalErrorsToDisplay.length)) {
          return React3.cloneElement(child, {
            role: "alert",
            ...child.props
          });
        }
        return React3.cloneElement(child, {
          role: "alert",
          children: globalErrorsToDisplay.map((error) => /* @__PURE__ */ jsx3(Error2, { children: error }, error)),
          ...child.props
        });
      }
      if (child.type === Button) {
        const onClick = ["button", "reset"].includes(child.props.type) ? void 0 : onSubmit;
        return React3.cloneElement(child, {
          disabled,
          children: buttonLabel,
          onClick,
          ...child.props
        });
      }
      return child;
    }
  );
  const defaultChildren = () => /* @__PURE__ */ jsxs2(Fragment2, { children: [
    Object.keys(schemaShape).map(makeField).map((field) => renderField({ Field, ...field })),
    (globalErrorsToDisplay == null ? void 0 : globalErrorsToDisplay.length) && /* @__PURE__ */ jsx3(Errors, { role: "alert", children: globalErrorsToDisplay.map((error) => /* @__PURE__ */ jsx3(Error2, { children: error }, error)) }),
    /* @__PURE__ */ jsx3(Button, { disabled, children: buttonLabel })
  ] });
  React3.useEffect(() => {
    const submitting = navigationState !== "idle";
    const shouldDisable = mode === "onChange" || mode === "all" ? submitting || !isValid : submitting;
    setDisabled(shouldDisable);
  }, [navigationState, formState, mode, isValid]);
  React3.useEffect(() => {
    const newDefaults = Object.fromEntries(
      reduceElements(customChildren, [], (prev, child) => {
        if (child.type === Field) {
          const { name, value } = child.props;
          prev.push([name, value]);
        }
        return prev;
      })
    );
    reset({ ...defaultValues, ...newDefaults });
  }, []);
  React3.useEffect(() => {
    Object.keys(errors).forEach((key) => {
      var _a2;
      form.setError(key, {
        type: "custom",
        message: ((_a2 = errors[key]) != null ? _a2 : []).join(", ")
      });
    });
    if (firstErroredField()) {
      try {
        form.setFocus(firstErroredField());
      } catch {
      }
    }
  }, [errorsProp, actionData]);
  React3.useEffect(() => {
    onNavigation == null ? void 0 : onNavigation(form);
  }, [navigationState]);
  return /* @__PURE__ */ jsx3(FormProvider, { ...form, children: /* @__PURE__ */ jsxs2(Component, { ref: formRef, method, onSubmit, ...props, children: [
    beforeChildren,
    customChildren != null ? customChildren : defaultChildren()
  ] }) });
}

// src/mutations.ts
import {
  inputFromForm,
  isInputError
} from "composable-functions";
import { data, redirect } from "react-router";
function errorMessagesForSchema(errors, _schema) {
  const inputErrors = errors.filter(isInputError);
  const nest = ({ path, messages }, root) => {
    var _a;
    const [head, ...tail] = path;
    root[head] = tail.length === 0 ? messages : nest(
      { path: tail, messages },
      (_a = root[head]) != null ? _a : {}
    );
    return root;
  };
  const compareStringArrays = (a) => (b) => JSON.stringify(a) === JSON.stringify(b);
  const toErrorObject = (errors2) => errors2.map(({ path, message }) => ({
    path,
    messages: [message]
  }));
  const unifyPaths = (errors2) => toErrorObject(errors2).reduce((memo, error) => {
    const comparePath = compareStringArrays(error.path);
    const mergeErrorMessages = ({ path, messages }) => comparePath(path) ? { path, messages: [...messages, ...error.messages] } : { path, messages };
    const existingPath = memo.find(({ path }) => comparePath(path));
    return existingPath ? memo.map(mergeErrorMessages) : [...memo, error];
  }, []);
  const errorTree = unifyPaths(inputErrors).reduce((memo, schemaError) => {
    const errorBranch = nest(schemaError, memo);
    return { ...memo, ...errorBranch };
  }, {});
  return errorTree;
}
async function getFormValues(request, schema) {
  const shape = objectFromSchema(schema).shape;
  const input = await inputFromForm(request);
  const values = {};
  for (const key in shape) {
    const value = input[key];
    values[key] = coerceValue(value, shape[key]);
  }
  return values;
}
async function performMutation({
  request,
  schema,
  mutation,
  context,
  transformResult = (result) => result,
  transformValues = (values) => values
}) {
  const values = await getFormValues(request, schema);
  const result = await mutation(transformValues(values), context);
  if (result.success) {
    return transformResult({ success: true, data: result.data });
  }
  const global = result.errors.filter((e) => !isInputError(e));
  return transformResult({
    success: false,
    errors: global.length > 0 ? {
      ...errorMessagesForSchema(result.errors, schema),
      _global: global.map((error) => error.message)
    } : errorMessagesForSchema(result.errors, schema),
    values
  });
}
async function formAction({
  successPath,
  ...performMutationOptions
}) {
  const result = await performMutation(performMutationOptions);
  if (result.success) {
    const path = typeof successPath === "function" ? await successPath(result.data) : successPath;
    if (path) {
      throw redirect(path);
    }
    return data(result);
  }
  return data(result, { status: 422 });
}
export {
  SchemaForm,
  formAction,
  performMutation,
  useField
};
